import { action, observable, makeObservable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import { IOrderByCondition } from 'Views/Components/ModelCollection/ModelQuery';
import * as Models from 'Models/Entities';
import { stringNotEmpty } from '../../Util/TypeGuards';
import { TimeUnit } from '../../Util/TimeUnit';

export interface IRouteEntityAttributes extends IModelAttributes {
	name: string;
	duration: number;
	disabled: boolean;
	closeVehicleBookingsBeforeDepartureValue: number;
	closeVehicleBookingsBeforeDepartureUnit: TimeUnit;
	closePassengerBookingsBeforeDepartureValue: number;
	closePassengerBookingsBeforeDepartureUnit: TimeUnit;
	closeFreightBookingsBeforeDepartureValue: number;
	closeFreightBookingsBeforeDepartureUnit: TimeUnit;
	mapId: string;
	map: Blob;
	mapCanvasCoordinates: string;

	additionalBookingSectionss: Array<
		Models.RoutesAdditionalBookingSections | Models.IRoutesAdditionalBookingSectionsAttributes
	>;
	configGeneral?: Models.ConfigGeneralEntity | Models.IConfigGeneralEntityAttributes;
	departureId: string;
	departure: Models.LocationEntity | Models.ILocationEntityAttributes;
	destinationId: string;
	destination: Models.LocationEntity | Models.ILocationEntityAttributes;
	feeOptions: Array<Models.RouteFeeOption | Models.IRouteFeeOptionAttributes>;
	ferryTrips: Array<Models.FerryTripEntity | Models.IFerryTripEntityAttributes>;
	prices: Array<Models.PriceEntity | Models.IPriceEntityAttributes>;
	bookingCutoffs: Array<Models.BookingCutoffRoute | Models.IBookingCutoffRouteAttributes>;
	organisations: Array<Models.OrganisationRoute | Models.IOrganisationRouteAttributes>;
	segments: Array<Models.SegmentEntity | Models.ISegmentEntityAttributes>;
	stops: Array<Models.StopEntity | Models.IStopEntityAttributes>;
	defaultPricingStrategyProfileId?: string;
	defaultPricingStrategyProfile?:
		| Models.PricingStrategyProfileEntity
		| Models.IPricingStrategyProfileEntityAttributes;
	checkInStatuses: Array<Models.CheckInStatusEntity | Models.ICheckInStatusEntityAttributes>;

	organisationIds?: Array<string>;
}
@entity('RouteEntity', 'Route')
export default class RouteEntity extends Model implements IRouteEntityAttributes {
	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [];

	@observable
	@attribute<RouteEntity, string>()
	public name: string;

	/**
	 * Duration of trip in minutes
	 */
	@observable
	@attribute<RouteEntity, number>()
	public duration: number;

	@observable
	@attribute<RouteEntity, boolean>()
	public disabled: boolean;

	/**
	 * How long before the departure time on this route will customer no longer be able to make vehicle bookings on the route.
	 */
	@observable
	@attribute<RouteEntity, number>()
	public closeVehicleBookingsBeforeDepartureValue: number;

	@observable
	@attribute<RouteEntity, TimeUnit>()
	public closeVehicleBookingsBeforeDepartureUnit: TimeUnit = TimeUnit.MINUTES;

	/**
	 * How long before the departure time on this route will customer no longer be able to make passenger bookings on the route.
	 */
	@observable
	@attribute<RouteEntity, number>()
	public closePassengerBookingsBeforeDepartureValue: number;

	@observable
	@attribute<RouteEntity, TimeUnit>()
	public closePassengerBookingsBeforeDepartureUnit: TimeUnit = TimeUnit.MINUTES;

	/**
	 * How long before the departure time on this route will customer no longer be able to make freight bookings on the route.
	 */
	@observable
	@attribute<RouteEntity, number>()
	public closeFreightBookingsBeforeDepartureValue: number;

	@observable
	@attribute<RouteEntity, TimeUnit>()
	public closeFreightBookingsBeforeDepartureUnit: TimeUnit = TimeUnit.MINUTES;

	@observable
	@attribute<RouteEntity, string>({ file: 'map' })
	public mapId: string;

	public map: Blob;
	@observable
	@attribute<RouteEntity, string>()
	public mapCanvasCoordinates: string;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public additionalBookingSectionss: Models.RoutesAdditionalBookingSections[] = [];

	@observable
	@attribute({ isReference: true, manyReference: false })
	public configGeneral?: Models.ConfigGeneralEntity;

	@observable
	@attribute()
	public departureId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public departure: Models.LocationEntity;

	@observable
	@attribute()
	public destinationId: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public destination: Models.LocationEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public feeOptions: Models.RouteFeeOption[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public ferryTrips: Models.FerryTripEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public prices: Models.PriceEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public bookingCutoffs: Models.BookingCutoffRoute[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public organisations: Models.OrganisationRoute[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public segments: Models.SegmentEntity[] = [];

	@observable
	@attribute({ isReference: true, manyReference: true })
	public stops: Models.StopEntity[] = [];

	@observable
	@attribute()
	public defaultPricingStrategyProfileId?: string;

	@observable
	@attribute({ isReference: true, manyReference: false })
	public defaultPricingStrategyProfile: Models.PricingStrategyProfileEntity;

	@observable
	@attribute({ isReference: true, manyReference: true })
	public checkInStatuses: Models.CheckInStatusEntity[] = [];

	public organisationIds?: string[] | undefined;

	constructor(attributes?: Partial<IRouteEntityAttributes>) {
		super(attributes);

		this.additionalBookingSectionss = [];
		this.feeOptions = [];
		this.ferryTrips = [];
		this.prices = [];
		this.bookingCutoffs = [];
		this.organisations = [];
		this.segments = [];
		this.stops = [];
		this.checkInStatuses = [];

		makeObservable(this);

		this.assignAttributes(attributes);
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	public assignAttributes(attributes?: Partial<IRouteEntityAttributes>) {
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.name !== undefined) {
				this.name = attributes.name;
			}
			if (attributes.duration !== undefined) {
				this.duration = attributes.duration;
			}
			if (attributes.disabled !== undefined) {
				this.disabled = attributes.disabled;
			}
			if (attributes.closeVehicleBookingsBeforeDepartureValue !== undefined) {
				this.closeVehicleBookingsBeforeDepartureValue = attributes.closeVehicleBookingsBeforeDepartureValue;
			}
			if (attributes.closeVehicleBookingsBeforeDepartureUnit !== undefined) {
				this.closeVehicleBookingsBeforeDepartureUnit = attributes.closeVehicleBookingsBeforeDepartureUnit;
			}
			if (attributes.closePassengerBookingsBeforeDepartureValue !== undefined) {
				this.closePassengerBookingsBeforeDepartureValue = attributes.closePassengerBookingsBeforeDepartureValue;
			}
			if (attributes.closePassengerBookingsBeforeDepartureUnit !== undefined) {
				this.closePassengerBookingsBeforeDepartureUnit = attributes.closePassengerBookingsBeforeDepartureUnit;
			}
			if (attributes.closeFreightBookingsBeforeDepartureValue !== undefined) {
				this.closeFreightBookingsBeforeDepartureValue = attributes.closeFreightBookingsBeforeDepartureValue;
			}
			if (attributes.closeFreightBookingsBeforeDepartureUnit !== undefined) {
				this.closeFreightBookingsBeforeDepartureUnit = attributes.closeFreightBookingsBeforeDepartureUnit;
			}
			if (attributes.map !== undefined) {
				this.map = attributes.map;
			}
			if (attributes.mapId !== undefined) {
				this.mapId = attributes.mapId;
			}
			if (attributes.mapCanvasCoordinates !== undefined) {
				this.mapCanvasCoordinates = attributes.mapCanvasCoordinates;
			}
			if (
				attributes.additionalBookingSectionss !== undefined &&
				Array.isArray(attributes.additionalBookingSectionss)
			) {
				if (!this.additionalBookingSectionss) {
					this.additionalBookingSectionss = [];
				}
				for (const model of attributes.additionalBookingSectionss) {
					if (model instanceof Models.RoutesAdditionalBookingSections) {
						this.additionalBookingSectionss.push(model);
					} else {
						this.additionalBookingSectionss.push(new Models.RoutesAdditionalBookingSections(model));
					}
				}
			}
			if (attributes.configGeneral !== undefined) {
				if (attributes.configGeneral === null) {
					this.configGeneral = attributes.configGeneral;
				} else if (attributes.configGeneral instanceof Models.ConfigGeneralEntity) {
					this.configGeneral = attributes.configGeneral;
				} else {
					this.configGeneral = new Models.ConfigGeneralEntity(attributes.configGeneral);
				}
			}
			if (attributes.departureId !== undefined) {
				this.departureId = attributes.departureId;
			}
			if (attributes.departure !== undefined) {
				if (attributes.departure === null) {
					this.departure = attributes.departure;
				} else if (attributes.departure instanceof Models.LocationEntity) {
					this.departure = attributes.departure;
					this.departureId = attributes.departure.id;
				} else {
					this.departure = new Models.LocationEntity(attributes.departure);
					this.departureId = this.departure.id;
				}
			}
			if (attributes.destinationId !== undefined) {
				this.destinationId = attributes.destinationId;
			}
			if (attributes.destination !== undefined) {
				if (attributes.destination === null) {
					this.destination = attributes.destination;
				} else if (attributes.destination instanceof Models.LocationEntity) {
					this.destination = attributes.destination;
					this.destinationId = attributes.destination.id;
				} else {
					this.destination = new Models.LocationEntity(attributes.destination);
					this.destinationId = this.destination.id;
				}
			}
			if (attributes.feeOptions !== undefined && Array.isArray(attributes.feeOptions)) {
				if (!this.feeOptions) {
					this.feeOptions = [];
				}
				for (const model of attributes.feeOptions) {
					if (model instanceof Models.RouteFeeOption) {
						this.feeOptions.push(model);
					} else {
						this.feeOptions.push(new Models.RouteFeeOption(model));
					}
				}
			}
			if (attributes.ferryTrips !== undefined && Array.isArray(attributes.ferryTrips)) {
				if (!this.ferryTrips) {
					this.ferryTrips = [];
				}
				for (const model of attributes.ferryTrips) {
					if (model instanceof Models.FerryTripEntity) {
						this.ferryTrips.push(model);
					} else {
						this.ferryTrips.push(new Models.FerryTripEntity(model));
					}
				}
			}
			if (attributes.prices !== undefined && Array.isArray(attributes.prices)) {
				if (!this.prices) {
					this.prices = [];
				}
				for (const model of attributes.prices) {
					if (model instanceof Models.PriceEntity) {
						this.prices.push(model);
					} else {
						this.prices.push(new Models.PriceEntity(model));
					}
				}
			}
			if (attributes.bookingCutoffs !== undefined && Array.isArray(attributes.bookingCutoffs)) {
				if (!this.bookingCutoffs) {
					this.bookingCutoffs = [];
				}
				for (const model of attributes.bookingCutoffs) {
					if (model instanceof Models.BookingCutoffRoute) {
						this.bookingCutoffs.push(model);
					} else {
						this.bookingCutoffs.push(new Models.BookingCutoffRoute(model));
					}
				}
			}
			if (attributes.organisations !== undefined && Array.isArray(attributes.organisations)) {
				if (!this.organisations) {
					this.organisations = [];
				}
				for (const model of attributes.organisations) {
					if (model instanceof Models.OrganisationRoute) {
						this.organisations.push(model);
					} else {
						this.organisations.push(new Models.OrganisationRoute(model));
					}
				}
			}
			if (attributes.segments !== undefined && Array.isArray(attributes.segments)) {
				if (!this.segments) {
					this.segments = [];
				}
				for (const model of attributes.segments) {
					if (model instanceof Models.SegmentEntity) {
						this.segments.push(model);
					} else {
						this.segments.push(new Models.SegmentEntity(model));
					}
				}
			}
			if (attributes.stops !== undefined && Array.isArray(attributes.stops)) {
				if (!this.stops) {
					this.stops = [];
				}
				for (const model of attributes.stops) {
					if (model instanceof Models.StopEntity) {
						this.stops.push(model);
					} else {
						this.stops.push(new Models.StopEntity(model));
					}
				}
			}
			if (attributes.defaultPricingStrategyProfileId !== undefined) {
				this.defaultPricingStrategyProfileId = attributes.defaultPricingStrategyProfileId;
			}
			if (attributes.defaultPricingStrategyProfile !== undefined) {
				if (attributes.defaultPricingStrategyProfile === null) {
					this.defaultPricingStrategyProfile = attributes.defaultPricingStrategyProfile;
				} else if (attributes.defaultPricingStrategyProfile instanceof Models.PricingStrategyProfileEntity) {
					this.defaultPricingStrategyProfile = attributes.defaultPricingStrategyProfile;
					this.defaultPricingStrategyProfileId = attributes.defaultPricingStrategyProfile.id;
				} else {
					this.defaultPricingStrategyProfile = new Models.PricingStrategyProfileEntity(
						attributes.defaultPricingStrategyProfile,
					);
					this.defaultPricingStrategyProfileId = this.defaultPricingStrategyProfile.id;
				}
			}
			if (attributes.checkInStatuses !== undefined && Array.isArray(attributes.checkInStatuses)) {
				if (!this.checkInStatuses) {
					this.checkInStatuses = [];
				}
				for (const model of attributes.checkInStatuses) {
					if (model instanceof Models.CheckInStatusEntity) {
						this.checkInStatuses.push(model);
					} else {
						this.checkInStatuses.push(new Models.CheckInStatusEntity(model));
					}
				}
			}
			if (stringNotEmpty(attributes.mapCanvasCoordinates)) {
				this.mapCanvasCoordinates = attributes.mapCanvasCoordinates;
			}
			if (attributes.organisationIds !== undefined && Array.isArray(attributes.organisationIds)) {
				this.organisationIds = attributes.organisationIds;
			}
		}
	}
}
